import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  MailOutline as MailOutlineIcon,
  Timeline as TimelineIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { useAuth } from '../contexts/AuthContext';
import {
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  formatParisDateTime,
} from '../utils/studentMessageBatches';
import StudentParcoursCalendar from '../components/studentTimeline/StudentParcoursCalendar';
import StudentParcoursTimeline from '../components/studentTimeline/StudentParcoursTimeline';
import {
  ParcoursViewMode,
  TimelineEvent,
} from '../utils/studentTimeline';

type HistoryEntry = {
  id: string;
  language: string;
  subject: string;
  status: 'sent' | 'failed' | 'scheduled' | 'skipped';
  errorMessage?: string;
  templateKey?: string;
  source?: 'manual' | 'automatic';
  triggerKey?: string;
  skipReason?: string;
  sentAt: string;
  scheduledAt?: string;
  batchId?: string;
  sentBy?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
};

const STATUS_LABELS: Record<HistoryEntry['status'], string> = {
  sent: 'Envoyé',
  failed: 'Échec',
  scheduled: BATCH_STATUS_LABELS.scheduled,
  skipped: 'Non envoyé',
};

const STATUS_COLORS: Record<
  HistoryEntry['status'],
  'success' | 'error' | 'primary' | 'default' | 'warning'
> = {
  sent: 'success',
  failed: 'error',
  scheduled: BATCH_STATUS_COLORS.scheduled as 'primary',
  skipped: 'warning',
};

const SKIP_REASON_LABELS: Record<string, string> = {
  opt_out_global: 'Relances désactivées (apprenant)',
  opt_out_course: 'Relances désactivées (formation)',
  no_email: 'Pas d\'adresse email',
  formation_disabled: 'Relances désactivées (formation)',
  trigger_disabled: 'Trigger désactivé',
};

const StudentMessageHistory: React.FC = () => {
  const { courseId, studentId } = useParams<{ courseId: string; studentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [viewMode, setViewMode] = useState<ParcoursViewMode>('list');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!courseId || !studentId) return;
    void (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminFetch(
          `${apiBase}/student-messages/courses/${courseId}/students/${studentId}/history`
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || data?.message || 'Impossible de charger l\'historique');
        }
        setHistory(data.history || []);
        setTimelineEvents(data.timelineEvents || []);
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, courseId, studentId]);

  const handleSendMessage = () => {
    if (!courseId || !studentId) return;
    navigate(`/courses/${courseId}/student-messages?students=${studentId}`);
  };

  const handleTimelineEventClick = (event: TimelineEvent) => {
    if (!isAdmin) return;
    if (event.category === 'message' && event.payload?.batchId) {
      navigate(`/student-messages/batches/${String(event.payload.batchId)}`);
    }
  };

  if (loading) {
    return <Typography color="text.secondary">Chargement…</Typography>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        mb={2}
      >
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={(_, value: ParcoursViewMode | null) => {
            if (value) setViewMode(value);
          }}
          size="small"
        >
          <ToggleButton value="list">
            <ViewListIcon fontSize="small" sx={{ mr: 0.75 }} />
            Liste
          </ToggleButton>
          <ToggleButton value="calendar">
            <CalendarMonthIcon fontSize="small" sx={{ mr: 0.75 }} />
            Calendrier
          </ToggleButton>
          <ToggleButton value="timeline">
            <TimelineIcon fontSize="small" sx={{ mr: 0.75 }} />
            Timeline
          </ToggleButton>
        </ToggleButtonGroup>

        {isAdmin && (
          <Button variant="contained" startIcon={<MailOutlineIcon />} onClick={handleSendMessage}>
            Planifier ou envoyer un message
          </Button>
        )}
      </Stack>

      <Card>
        <CardContent>
          {viewMode === 'list' && (
            <>
              {history.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Aucun message envoyé ou planifié pour le moment.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Objet</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Langue</TableCell>
                        <TableCell>Erreur</TableCell>
                        <TableCell>Créé par</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((entry) => {
                        const isScheduled = entry.status === 'scheduled';
                        const displayDate = isScheduled
                          ? entry.scheduledAt || entry.sentAt
                          : entry.sentAt;

                        return (
                          <TableRow
                            key={entry.id}
                            hover={isScheduled && !!entry.batchId && isAdmin}
                            sx={
                              isScheduled && entry.batchId && isAdmin
                                ? { cursor: 'pointer' }
                                : undefined
                            }
                            onClick={
                              isScheduled && entry.batchId && isAdmin
                                ? () => navigate(`/student-messages/batches/${entry.batchId}`)
                                : undefined
                            }
                          >
                            <TableCell>
                              {isScheduled ? (
                                <Box>
                                  <Typography variant="body2">
                                    {formatParisDateTime(displayDate)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Envoi prévu
                                  </Typography>
                                </Box>
                              ) : (
                                new Date(displayDate).toLocaleString('fr-FR')
                              )}
                            </TableCell>
                            <TableCell>{entry.subject}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small"
                                  color={STATUS_COLORS[entry.status]}
                                  label={STATUS_LABELS[entry.status]}
                                />
                                {entry.source === 'automatic' && (
                                  <Chip size="small" variant="outlined" label="Automatique" />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>{entry.language}</TableCell>
                            <TableCell>
                              {entry.errorMessage ||
                                (entry.skipReason
                                  ? SKIP_REASON_LABELS[entry.skipReason] || entry.skipReason
                                  : '—')}
                            </TableCell>
                            <TableCell>
                              {entry.source === 'automatic'
                                ? 'Automatique'
                                : entry.sentBy
                                  ? `${entry.sentBy.firstName} ${entry.sentBy.lastName}`
                                  : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {viewMode === 'calendar' && (
            <StudentParcoursCalendar
              events={timelineEvents}
              onEventClick={handleTimelineEventClick}
            />
          )}

          {viewMode === 'timeline' && (
            <StudentParcoursTimeline
              events={timelineEvents}
              onEventClick={handleTimelineEventClick}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentMessageHistory;
